import type { Metric } from 'web-vitals';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    Cookiebot?: {
      consent?: {
        statistics?: boolean;
      };
    };
  }
}

export type AnalyticsSourceSurface = 'site' | 'app' | 'api' | 'worker';
export type AnalyticsEventOwner =
  | 'frontend_intent'
  | 'backend_truth'
  | 'diagnostic';

export type AnalyticsScalar = string | number | boolean | null | undefined;
export type AnalyticsProperties = Record<string, AnalyticsScalar>;

export type AnalyticsEvent = {
  eventKey: string;
  occurredAt: number;
  sourceSurface: AnalyticsSourceSurface;
  eventOwner: AnalyticsEventOwner;
  userId?: string;
  anonymousId?: string;
  sessionId?: string;
  subscriptionPlan?: string;
  isPayingUser?: boolean;
  routePath?: string;
  marketplaceName?: string;
  entityType?: string;
  entityId?: string;
  failureCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrer?: string;
  properties: AnalyticsProperties;
};

type AnalyticsTrackOptions = {
  eventOwner?: AnalyticsEventOwner;
  routePath?: string;
  marketplaceName?: string;
  entityType?: string;
  entityId?: string;
  failureCode?: string;
  nonInteraction?: boolean;
};

type AnalyticsInitOptions = {
  measurementId?: string;
  sourceSurface: Extract<AnalyticsSourceSurface, 'site' | 'app'>;
  appName: string;
  enabled?: boolean;
};

type AcquisitionContext = {
  landingPath?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
};

const DEFAULT_MEASUREMENT_ID = 'G-TQKBBPBHLC';

const SESSION_STORAGE_KEY = 'tnl-analytics-session-id';
const ANONYMOUS_STORAGE_KEY = 'tnl-analytics-anonymous-id';
const ACQUISITION_STORAGE_KEY = 'tnl-analytics-acquisition';

const GA_PARAM_WHITELIST = new Set([
  'source_surface',
  'event_owner',
  'route_group',
  'route_path',
  'subscription_plan',
  'is_paying_user',
  'marketplace_name',
  'failure_code',
  'selected_plan',
  'billing_interval',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'referrer_domain',
  'billing_state',
  'lifecycle_stage',
  'churn_reason_primary',
  'feature_name',
  'action_type',
  'cta_location',
  'page_kind',
  'permission_status',
  'extension_status',
  'platform',
  'entity_type',
  'account_state',
  'retention_state',
  'value_state',
  'value_path',
  'value_definition_version',
  'sync_type',
  'sync_status',
  'onboarding_step',
  'feature_area',
  'days_since_signup',
  'days_since_last_activity',
  'days_to_value',
  'record_count_bucket',
  'connection_count_bucket',
  'failure_stage',
  'churn_type',
  'winback_candidate',
]);

const runtime = {
  appName: 'TrackNList',
  sourceSurface: 'app' as Extract<AnalyticsSourceSurface, 'site' | 'app'>,
  measurementId: DEFAULT_MEASUREMENT_ID,
  enabled: true,
  initialized: false,
  currentUserId: '',
  currentPlan: '',
  currentPayingState: false,
};

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isLocalhost() {
  if (!isBrowser()) return false;
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '::1' ||
    host.endsWith('.local')
  );
}

function createId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `tnl-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function getOrCreateStorageValue(
  storage: Storage | undefined,
  key: string,
  valueFactory: () => string
) {
  if (!storage) {
    return valueFactory();
  }
  const existingValue = storage.getItem(key);
  if (existingValue) {
    return existingValue;
  }
  const nextValue = valueFactory();
  storage.setItem(key, nextValue);
  return nextValue;
}

export function getAnonymousId() {
  if (!isBrowser()) return '';
  try {
    return getOrCreateStorageValue(
      window.localStorage,
      ANONYMOUS_STORAGE_KEY,
      createId
    );
  } catch {
    return createId();
  }
}

export function getSessionId() {
  if (!isBrowser()) return '';
  try {
    return getOrCreateStorageValue(
      window.sessionStorage,
      SESSION_STORAGE_KEY,
      createId
    );
  } catch {
    return createId();
  }
}

function normalizeKey(input: string) {
  return input
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
    .slice(0, 40);
}

function referrerDomain(value: string) {
  if (!value) return '';
  try {
    return new URL(value).hostname;
  } catch {
    return value;
  }
}

export function getRouteGroup(path: string) {
  const normalizedPath = (path || '').split('?')[0].split('#')[0];
  if (!normalizedPath || normalizedPath === '/') return 'home';
  if (normalizedPath.startsWith('/pricing')) return 'pricing';
  if (normalizedPath.startsWith('/signup')) return 'signup';
  if (normalizedPath.startsWith('/login')) return 'login';
  if (normalizedPath.startsWith('/forgot-password')) return 'forgot_password';
  if (normalizedPath.startsWith('/contact-us')) return 'contact';
  if (normalizedPath.startsWith('/privacy-policy')) return 'privacy_policy';
  if (normalizedPath.startsWith('/terms-and-conditions')) return 'terms';
  if (normalizedPath.startsWith('/about')) return 'about';
  if (normalizedPath.startsWith('/calculator')) return 'calculator';
  if (normalizedPath.startsWith('/public/share')) return 'public_share';
  if (normalizedPath.startsWith('/getting-started')) return 'getting_started';
  if (/^\/inventory\/[^/]+/.test(normalizedPath)) return 'product_detail';
  if (normalizedPath.startsWith('/inventory')) return 'inventory';
  if (normalizedPath.startsWith('/marketplaces')) return 'marketplaces';
  if (normalizedPath.startsWith('/sales')) return 'sales';
  if (normalizedPath.startsWith('/templates')) return 'templates';
  if (normalizedPath.startsWith('/collections')) return 'collections';
  if (normalizedPath.startsWith('/expenses')) return 'expenses';
  if (normalizedPath.startsWith('/reports')) return 'reports';
  if (normalizedPath.startsWith('/settings')) return 'settings';
  if (normalizedPath.startsWith('/profile')) return 'profile';
  if (normalizedPath.startsWith('/notifications')) return 'notifications';
  if (normalizedPath.startsWith('/messages')) return 'messages';
  if (normalizedPath.startsWith('/chats')) return 'chats';
  if (normalizedPath.startsWith('/linking')) return 'linking_review';
  if (normalizedPath.startsWith('/l/')) return 'magic_link';
  if (normalizedPath.startsWith('/tnl')) return 'tnl_entry';
  return normalizeKey(normalizedPath) || 'unknown';
}

function loadScript(measurementId: string) {
  if (!isBrowser()) return;
  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[data-tnl-analytics="${measurementId}"]`
  );
  if (existingScript) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.dataset.tnlAnalytics = measurementId;
  document.head.appendChild(script);
}

function ensureGtag() {
  if (!isBrowser()) return;
  if (typeof window.gtag === 'function') return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => {
    window.dataLayer?.push(args);
  };
}

export function updateAnalyticsConsent(granted: boolean) {
  if (!isBrowser() || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
    functionality_storage: granted ? 'granted' : 'denied',
    personalization_storage: granted ? 'granted' : 'denied',
    security_storage: 'granted',
  });
}

function hasStatisticsConsent() {
  if (!isBrowser()) return false;
  const consentValue = window.Cookiebot?.consent?.statistics;
  if (typeof consentValue === 'boolean') {
    return consentValue;
  }
  return true;
}

function isAnalyticsEnabled() {
  if (!runtime.enabled) return false;
  if (isLocalhost()) return false;
  return hasStatisticsConsent();
}

export function storeAcquisitionContext(
  overrides: Partial<AcquisitionContext> = {}
) {
  if (!isBrowser()) return;
  const params = new URLSearchParams(window.location.search);
  const nextValue: AcquisitionContext = {
    landingPath: window.location.pathname,
    referrer: document.referrer || '',
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    utmTerm: params.get('utm_term') || undefined,
    utmContent: params.get('utm_content') || undefined,
    ...overrides,
  };

  try {
    const currentValue = getAcquisitionContext();
    const mergedValue: AcquisitionContext = {
      ...currentValue,
      ...Object.fromEntries(
        Object.entries(nextValue).filter(([, value]) => Boolean(value))
      ),
    };
    window.localStorage.setItem(
      ACQUISITION_STORAGE_KEY,
      JSON.stringify(mergedValue)
    );
  } catch {}
}

export function getAcquisitionContext(): AcquisitionContext {
  if (!isBrowser()) return {};
  try {
    const rawValue = window.localStorage.getItem(ACQUISITION_STORAGE_KEY);
    if (!rawValue) return {};
    return JSON.parse(rawValue) as AcquisitionContext;
  } catch {
    return {};
  }
}

export function initializeAnalytics(options: AnalyticsInitOptions) {
  runtime.appName = options.appName;
  runtime.sourceSurface = options.sourceSurface;
  runtime.measurementId = options.measurementId || DEFAULT_MEASUREMENT_ID;
  runtime.enabled = options.enabled ?? true;

  if (!isBrowser()) return;

  ensureGtag();
  loadScript(runtime.measurementId);
  updateAnalyticsConsent(hasStatisticsConsent());

  if (!runtime.initialized) {
    window.gtag?.('js', new Date());
  }

  window.gtag?.('config', runtime.measurementId, {
    app_name: runtime.appName,
    send_page_view: false,
    user_id: runtime.currentUserId || undefined,
  });

  storeAcquisitionContext();
  runtime.initialized = true;
}

export function identifyAnalyticsUser(
  userId?: string | null,
  options: {
    subscriptionPlan?: string;
    isPayingUser?: boolean;
  } = {}
) {
  runtime.currentUserId = String(userId || '').trim();
  runtime.currentPlan = options.subscriptionPlan || runtime.currentPlan;
  runtime.currentPayingState =
    options.isPayingUser ?? runtime.currentPayingState;

  if (!isBrowser() || !runtime.initialized) return;
  window.gtag?.('config', runtime.measurementId, {
    user_id: runtime.currentUserId || undefined,
  });
}

function sanitizeGaParams(properties: AnalyticsProperties) {
  const gaParams: Record<string, AnalyticsScalar> = {};
  Object.entries(properties).forEach(([key, value]) => {
    if (value == null) return;
    const normalizedKey = normalizeKey(key);
    if (!normalizedKey || !GA_PARAM_WHITELIST.has(normalizedKey)) return;
    gaParams[normalizedKey] = value;
  });
  return gaParams;
}

export function toGaEventName(eventKey: string) {
  const normalizedName = normalizeKey(eventKey.replace(/:/g, '_'));
  if (!normalizedName) {
    return 'tracknlist_event';
  }
  if (!/^[a-z]/.test(normalizedName)) {
    return `tnl_${normalizedName}`.slice(0, 40);
  }
  return normalizedName.slice(0, 40);
}

export function buildAnalyticsEvent(
  eventKey: string,
  properties: AnalyticsProperties = {},
  options: AnalyticsTrackOptions = {}
): AnalyticsEvent {
  const routePath =
    options.routePath || (isBrowser() ? window.location.pathname : '');
  const acquisition = getAcquisitionContext();
  return {
    eventKey,
    occurredAt: Date.now(),
    sourceSurface: runtime.sourceSurface,
    eventOwner: options.eventOwner || 'frontend_intent',
    userId: runtime.currentUserId || undefined,
    anonymousId: getAnonymousId() || undefined,
    sessionId: getSessionId() || undefined,
    subscriptionPlan: runtime.currentPlan || undefined,
    isPayingUser: runtime.currentPayingState,
    routePath: routePath || undefined,
    marketplaceName: options.marketplaceName || undefined,
    entityType: options.entityType || undefined,
    entityId: options.entityId || undefined,
    failureCode: options.failureCode || undefined,
    utmSource: acquisition.utmSource,
    utmMedium: acquisition.utmMedium,
    utmCampaign: acquisition.utmCampaign,
    referrer: acquisition.referrer || undefined,
    properties,
  };
}

export function track(
  eventKey: string,
  properties: AnalyticsProperties = {},
  options: AnalyticsTrackOptions = {}
) {
  const analyticsEvent = buildAnalyticsEvent(eventKey, properties, options);

  if (!isBrowser() || !runtime.initialized || !isAnalyticsEnabled()) {
    return analyticsEvent;
  }

  const gaParams = sanitizeGaParams({
    source_surface: analyticsEvent.sourceSurface,
    event_owner: analyticsEvent.eventOwner,
    route_group: getRouteGroup(analyticsEvent.routePath || ''),
    route_path: analyticsEvent.routePath,
    subscription_plan: analyticsEvent.subscriptionPlan,
    is_paying_user: analyticsEvent.isPayingUser,
    marketplace_name: analyticsEvent.marketplaceName,
    failure_code: analyticsEvent.failureCode,
    utm_source: analyticsEvent.utmSource,
    utm_medium: analyticsEvent.utmMedium,
    utm_campaign: analyticsEvent.utmCampaign,
    referrer_domain: referrerDomain(analyticsEvent.referrer || ''),
    entity_type: analyticsEvent.entityType,
    ...properties,
  });

  if (options.nonInteraction) {
    gaParams.non_interaction = true;
  }

  window.gtag?.('event', toGaEventName(eventKey), gaParams);
  return analyticsEvent;
}

export function trackFeatureUsed(
  featureName: string,
  actionType = 'used',
  properties: AnalyticsProperties = {},
  options: AnalyticsTrackOptions = {}
) {
  return track(
    'FEATURE:USED',
    {
      feature_name: featureName,
      feature_area: properties.feature_area || featureName,
      action_type: actionType,
      ...properties,
    },
    options
  );
}

export function trackPageView(
  path: string,
  options: {
    title?: string;
    pageKind?: string;
    eventKey?: string;
  } = {}
) {
  const eventKey =
    options.eventKey ||
    (runtime.sourceSurface === 'site'
      ? 'SITE:PAGE_VIEWED'
      : 'APP:ROUTE_VIEWED');

  return track(
    eventKey,
    {
      page_kind: options.pageKind || getRouteGroup(path),
      route_path: path,
    },
    {
      routePath: path,
      eventOwner: 'frontend_intent',
      nonInteraction: true,
    }
  );
}

export function trackWebVital(metric: Metric) {
  track(
    'TECH:WEB_VITAL_RECORDED',
    {
      feature_name: metric.name,
      value: Math.round(
        metric.name === 'CLS' ? metric.value * 1000 : metric.value
      ),
      page_kind: getRouteGroup(isBrowser() ? window.location.pathname : ''),
    },
    {
      eventOwner: 'diagnostic',
      nonInteraction: true,
    }
  );
}

export function getAnalyticsHeaders(routePath = '') {
  const acquisition = getAcquisitionContext();
  return {
    'x-tnl-session-id': getSessionId(),
    'x-tnl-anonymous-id': getAnonymousId(),
    'x-tnl-client-surface': runtime.sourceSurface,
    'x-tnl-route': routePath || (isBrowser() ? window.location.pathname : ''),
    'x-tnl-acquisition-context': JSON.stringify(acquisition),
  };
}

export function getAnalyticsState() {
  return {
    sourceSurface: runtime.sourceSurface,
    measurementId: runtime.measurementId,
    userId: runtime.currentUserId,
    anonymousId: getAnonymousId(),
    sessionId: getSessionId(),
  };
}
